import React, { FC, useCallback, useState } from 'react';
import { getDayEnd, getDayStart, useDataProvider } from 'src/app/common/utils';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import { PaginateListMore } from 'src/app/common/types';
import { exportLeadListDownload, fetchLeadList, LeadListParam } from 'src/app/modules/PulseLeads/network/autoLeadReassignmentCrud';
import RulesLeadList from './components/RulesLeadList';
import { RulesDistributionStatus, RulesLeadListItem, RulesType } from 'src/app/modules/PulseLeads/types/auto-lead-reassignment-types';
import { RouteComponentProps, useHistory, useParams } from 'react-router-dom';
import { autoLeadReassignmentStatusRulePath, autoLeadReassignmentTerminatedAgentRulePath } from '../../AutoLeadReassignmentRoutes';
import moment from 'moment';

type RulesLeadListingPageProps = {} & ParamsProps & RouteComponentProps & {
  ruleType: RulesType;
};

const initialState: LeadListParam = {
  leadDateFrom: moment().startOf('day').subtract(7, 'days').toDate(),
  leadDateTo: getDayEnd(new Date()),
  limit: 5,
  page: 1,
};

const paramsInitiator = (initialParams?: Record<string, string>): LeadListParam => {
  return initialParams
    ? {
      leadDateFrom: initialParams.leadDateFrom
        ? getDayStart(new Date(initialParams.leadDateFrom))
        : moment().startOf('day').subtract(7, 'days').toDate(),
      leadDateTo: initialParams.leadDateTo
        ? getDayEnd(new Date(initialParams.leadDateTo))
        : getDayEnd(new Date()),
      page: 1,
      limit: 5,
    }
    : initialState;
};

const RulesLeadListingPage: FC<RulesLeadListingPageProps> = ({ onChangeQueryParams, ruleType }) => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const defaultFilterValue = paramsInitiator();
  const [filterState, setFilterState] = useState<LeadListParam>(defaultFilterValue);
  const [dataList, setDataList] =
    useState<PaginateListMore<RulesLeadListItem>>();

  const { isLoading, refreshData } = useDataProvider<PaginateListMore<RulesLeadListItem>>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await fetchLeadList(ruleType, id, filterState, dispatch);
      } catch (err) { }
    },
    setDataList,
    false,
  );

  const exportLeadList = useCallback(() => {
    return exportLeadListDownload(ruleType, id, filterState, dispatch);
  }, [dataList]);

  return (
    <>
      <PruFilter
        title={Translation(`pulseleads.leadReassignment.filter`)}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'alias',
            initialValue: filterState.alias,
            displayName: 'Full name',
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentCodeFrom',
            initialValue: filterState.agentCodeFrom,
            displayName: 'From Agent code',
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentCodeTo',
            initialValue: filterState.agentCodeTo,
            displayName: 'Assigned Agent code',
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'leadDateFrom',
            fieldTo: 'leadDateTo',
            initialDateFrom: filterState.leadDateFrom,
            initialDateTo: filterState.leadDateTo,
            defaultDateFrom: defaultFilterValue.leadDateFrom,
            defaultDateTo: defaultFilterValue.leadDateTo,
            displayName: 'Processing date',
            periodTime: { months: 3 },
            required: true,
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'status',
            displayName: 'Distribution status',
            choices:
              [
                { displayName: 'All', value: '' },
                { displayName: RulesDistributionStatus.queue, value: RulesDistributionStatus.queue },
                { displayName: RulesDistributionStatus.distributing, value: RulesDistributionStatus.distributing },
                { displayName: RulesDistributionStatus.distributed, value: RulesDistributionStatus.distributed },
                { displayName: RulesDistributionStatus.failed, value: RulesDistributionStatus.failed },
              ],
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            alias: data.alias,
            agentCodeFrom: data.agentCodeFrom,
            agentCodeTo: data.agentCodeTo,
            leadDateFrom: data.leadDateFrom,
            leadDateTo: data.leadDateTo,
            status: data.status ? data.status as RulesDistributionStatus : undefined,
          })
        }
        fetchData={refreshData}
        enableBack
        onBack={() => history.push(
          ruleType === RulesType.statusRule
            ? `${autoLeadReassignmentStatusRulePath}`
            : `${autoLeadReassignmentTerminatedAgentRulePath}`
        )}
      />
      <RulesLeadList
        isLoading={isLoading}
        dataList={dataList}
        exportLeadList={exportLeadList}
        onRefresh={() => {
          refreshData();
        }}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default RulesLeadListingPage;
